/**
 * Lightweight fetch interceptor over `window.fetch`, doing two things.
 *
 * 1. Keeps a rolling buffer of the last N browser-level HTTP requests, used by the Feedback chat
 *    agent to attach recent network activity to bug reports. Only URL, method, status code, duration
 *    and (on failure) error message are stored — response bodies are intentionally omitted to avoid
 *    capturing PII.
 * 2. Injects headers supplied by {@link setRequestHeaderProvider} onto same-origin API requests.
 *    This is how PR-env routing headers reach the ~9 file-download flows that call `fetch` directly
 *    instead of going through `apiClient` (INF-856) — exactly the flows an operator wants to test
 *    against an ephemeral environment. When no provider is set, or it returns nothing, the request is
 *    passed through completely untouched.
 */

export interface INetworkRequest {
  url: string;
  method: string;
  status: number | null;
  timestamp: string;
  duration_ms: number;
  error?: string;
}

const MAX_BUFFER = 30;
const _buffer: INetworkRequest[] = [];

/** Everything the panel API is served under. Matches `env.API_BASE_URL`. */
const API_PATH_PREFIX = '/panel';

export interface IRequestSnapshot {
  successful: INetworkRequest[];
  errors: INetworkRequest[];
}

/**
 * Returns the last N successful (2xx) and last N error (non-2xx / network failure)
 * requests as separate arrays so the AI agent can focus on what matters.
 */
export const getRequestSnapshot = (limit = 7): IRequestSnapshot => {
  const successful = _buffer.filter((r) => r.status !== null && r.status >= 200 && r.status < 300);
  const errors = _buffer.filter((r) => r.status === null || r.status < 200 || r.status >= 300);
  return {
    successful: successful.slice(-limit),
    errors: errors.slice(-limit),
  };
};

let _installed = false;

type THeaderProvider = () => Record<string, string>;

let _headerProvider: THeaderProvider | null = null;

/**
 * Registers a source of extra headers for same-origin API requests, or clears it with `null`.
 *
 * Kept generic on purpose: this module knows nothing about PR environments, only that something may
 * want headers on outbound API calls. See `@bootstrap/pr-env/install-pr-env-headers`.
 */
export const setRequestHeaderProvider = (provider: THeaderProvider | null): void => {
  _headerProvider = provider;
};

/**
 * Same-origin API calls only.
 *
 * Cross-origin is excluded because adding a custom header there turns the request into a preflighted
 * one, which would break third-party calls outright. Relative URLs are same-origin by definition.
 */
const _acceptsExtraHeaders = (url: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const resolved = new URL(url, window.location.origin);
    return resolved.origin === window.location.origin && resolved.pathname.startsWith(API_PATH_PREFIX);
  } catch {
    return false;
  }
};

/**
 * Merges the provider's headers into a request, never overwriting one already set.
 *
 * `apiClient` calls this patched `fetch` too, so a request can pass through both the apiClient
 * interceptor and this patch. Never-overwrite is what makes that idempotent rather than conflicting.
 */
const _withExtraHeaders = (
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  extra: Record<string, string>
): { input: RequestInfo | URL; init?: RequestInit } => {
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  Object.entries(extra).forEach(([name, value]) => {
    if (!headers.has(name)) headers.set(name, value);
  });

  // A Request must be rebuilt rather than mutated; going through the constructor clones the body per
  // spec instead of consuming the stream.
  if (input instanceof Request && !init?.headers) {
    return { input: new Request(input, { headers }), init };
  }
  return { input, init: { ...init, headers } };
};

/** Call once at app startup.  Safe to call multiple times — installs only once. */
export const installNetworkInterceptor = (): void => {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  const _origFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;

    const method = (
      init?.method ?? (input instanceof Request ? input.method : 'GET')
    ).toUpperCase();

    const extra = _headerProvider && _acceptsExtraHeaders(url) ? _headerProvider() : {};
    if (Object.keys(extra).length > 0) {
      const patched = _withExtraHeaders(input, init, extra);
      input = patched.input;
      init = patched.init;
    }

    const start = Date.now();
    const timestamp = new Date().toISOString();

    try {
      const res = await _origFetch(input, init);
      if (_isApiCall(url)) {
        _push({ url, method, status: res.status, timestamp, duration_ms: Date.now() - start });
      }
      return res;
    } catch (err) {
      if (_isApiCall(url)) {
        _push({
          url,
          method,
          status: null,
          timestamp,
          duration_ms: Date.now() - start,
          error: err instanceof Error ? err.message : 'Network error',
        });
      }
      throw err;
    }
  };
};

const _IGNORE = /\/(me|my\/modules|auth\/init|auth\/|locales\/|favicon|\.ico|\.png|\.js|\.css|\.woff)/i;

const _isApiCall = (url: string): boolean =>
  /\/v1\/api\//.test(url) && !_IGNORE.test(url);

const _push = (req: INetworkRequest): void => {
  _buffer.push(req);
  if (_buffer.length > MAX_BUFFER) _buffer.shift();
};
